import {isDraftUploadVariation} from "./bgIdentity";
import type {BackgroundId} from "./selectorVisibility";

// ---------------------------------------------------------------------------
// The number of a background is its place among the tiles of the page
// (Sergey, 2026-10-26): `?bg=3` is the third tile. A drag that moves a tile to
// another place gives it the number of that place, and the other tiles get new
// numbers. Inside, the background keeps its identity (its catalog variation):
// `bgOption`, the default scenes, the hidden marks and the set orders hold it,
// so a reorder changes no default. Only the URL holds a number.
//
// The numbered tiles are the gallery tiles of the page: in the set's order,
// without the hidden ones and without the temporary upload tiles. A background
// that has no number (an upload in the shared slot, the generative gradient,
// a temporary upload tile, a hidden background on a saved link) keeps its
// identity in `?background=`. Old links and the tools name a background by
// identity too: `?background=1001` opens it, and the page then shows its
// number.
// ---------------------------------------------------------------------------

/** The URL param that holds the number of a tile. */
export const BG_NUMBER_PARAM = "bg";

/** The URL param that holds the identity of a background. */
export const BG_IDENTITY_PARAM = "background";

/** The background that a URL names. */
export type UrlBackground =
  | {kind: "number"; number: number}
  | {kind: "identity"; background: string};

/**
 * The background that the URL names, or null. A number is a positive integer
 * in `?bg=`; any other `?bg=` value names nothing.
 */
export function readUrlBackground(params: URLSearchParams): UrlBackground | null {
  const number = params.get(BG_NUMBER_PARAM);
  if (number !== null) {
    return /^[1-9]\d*$/.test(number) ? {kind: "number", number: Number(number)} : null;
  }
  const background = params.get(BG_IDENTITY_PARAM);
  return background ? {kind: "identity", background} : null;
}

/** True when the URL names a background, by number or by identity. */
export function namesUrlBackground(params: URLSearchParams): boolean {
  return params.has(BG_NUMBER_PARAM) || params.has(BG_IDENTITY_PARAM);
}

/**
 * The variations of the numbered tiles, in tile order: `tiles` without the
 * hidden backgrounds and the temporary upload tiles. Tile N is entry N - 1.
 */
export function numberedVariations(
  tiles: readonly number[],
  hidden: ReadonlySet<BackgroundId>
): number[] {
  return tiles.filter((variation) => !hidden.has(variation) && !isDraftUploadVariation(variation));
}

/** The identity of tile `number`, or null when the page has no such tile. */
export function backgroundOfNumber(number: number, numbered: readonly number[]): string | null {
  const variation = numbered[number - 1];
  return variation === undefined ? null : String(variation);
}

/** The number of a background, or null when it has no tile with a number. */
export function numberOfBackground(
  background: string,
  numbered: readonly number[]
): number | null {
  if (!/^\d+$/.test(background)) return null;
  const index = numbered.indexOf(Number(background));
  return index < 0 ? null : index + 1;
}

/**
 * True when the page has a tile for `background`: a numbered tile, the
 * upload slot ("custom"), the generative gradient when the page offers it, or
 * a temporary upload tile (the selector drops one that left the list). Any
 * other background does not exist for the page: a hidden one, one that the
 * gallery does not hold (an admin's list draft removed it), one with no image.
 */
export function hasPageTile(
  background: string,
  numbered: readonly number[],
  offersGenerative: boolean
): boolean {
  if (background === "custom") return true;
  if (background === "generative") return offersGenerative;
  if (!/^\d+$/.test(background)) return false;
  return isDraftUploadVariation(Number(background)) || numbered.includes(Number(background));
}

/**
 * Write `background` into `params`: its number in `?bg=` when it has one,
 * else its identity in `?background=`. `null` takes the background out of the
 * URL. Without the numbered tiles (they are not known yet) the identity goes
 * into the URL.
 */
export function writeUrlBackground(
  params: URLSearchParams,
  background: string | null,
  numbered: readonly number[] | null
): URLSearchParams {
  params.delete(BG_NUMBER_PARAM);
  params.delete(BG_IDENTITY_PARAM);
  if (background === null) return params;
  const number = numbered ? numberOfBackground(background, numbered) : null;
  if (number !== null) params.set(BG_NUMBER_PARAM, String(number));
  else params.set(BG_IDENTITY_PARAM, background);
  return params;
}

/**
 * How the UI names a background: its tile number, the name of a background
 * without a gallery tile ("custom", "generative"), or "–" for a gallery
 * background that has no tile on the page.
 */
export function backgroundLabel(background: string, numbered: readonly number[] | null): string {
  const number = numbered ? numberOfBackground(background, numbered) : null;
  if (number !== null) return String(number);
  return /^\d+$/.test(background) ? "–" : background;
}
